import React, { useCallback, useEffect } from 'react'
import { MenuButtonsWrapper, MenuProduct, MenuProductsList, MenuProductTitle, MenuSelectionTitle, MenuSelectionWrapper } from './index.styled'
import { batch, useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { setCartCategoryCategoryId, setCartCategoryProductType } from '../../../../../redux/action/cartCategory'
import { setCartProductMenu, setCartProductMenuProductIndex } from '../../../../../redux/action/cartProduct'
import { calcCartItemTotalPrice, clone, getPosPriceByIndex, roundPrice } from '../../../../../helper'
import Price from '../../../blocks/Price'
import Button from '../../../buttons/Button'
import MenuItem from './MenuItem'
import { selectIsNeededZIndex } from '../../../../../redux/selector/system'
import KeyBindings from '../../../keybindings'
import MainIcon from '../../../../../img/svg/main'
import { getCartProductThunk } from '../../../../../redux/thunk/cartProduct'
import { getConvertedMenu2Item } from '../../../../../helper/convertor'
import { useTheme } from 'styled-components'

const EVENT_Z_INDEX = 0

const MenuSelection = React.memo(({ orderType, addMainDish, menuSelectionRef, module }) => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const { colorTheme } = useTheme()

	const menu = useSelector(state => state.cartProduct.menu)
	const menuProductIndex = useSelector(state => state.cartProduct.menuProductIndex)
	const isNeededZIndex = useSelector(selectIsNeededZIndex(EVENT_Z_INDEX))
	const menuPrice = getPosPriceByIndex(menu, orderType, 0)?.value
	const menuFinished = !menu?.menuPositions?.find(m => !m.item)
	const menuNotEmpty = menu?.menuPositions?.find(m => m.item)
	const menuItemsTotal = menu?.menuPositions.reduce((tp, i) => tp + (i?.item ? calcCartItemTotalPrice(i.item) : 0), 0)
	const menuTotalPrice = roundPrice(menuPrice + menuItemsTotal)

	useEffect(() => {
		if (menu) {
			const menuProduct = menu.menuPositions[menuProductIndex]
			if (menuProduct) {
				dispatch(setCartCategoryCategoryId(menuProduct.categoryId))
			} else {
				dispatch(setCartCategoryCategoryId(null))
			}
			dispatch(setCartCategoryProductType(null))
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [menu, menuProductIndex])

	const close = useCallback(() => {
		batch(() => {
			dispatch(setCartProductMenu(null))
			dispatch(setCartProductMenuProductIndex(0))
			dispatch(setCartCategoryCategoryId(null))
			dispatch(setCartCategoryProductType(null))
		})
	}, [dispatch])

	const setMenuItemFocus = useCallback((index, product) => {
		dispatch(setCartProductMenuProductIndex(index))
		if (product.numberOfMustExtras > 0 || !product.disableCartModal) {
			dispatch(getCartProductThunk(product))
		}
	}, [dispatch])

	const setMenuProductFocus = useCallback((index) => {
		dispatch(setCartProductMenuProductIndex(index))
	}, [dispatch])

	const addMenu = useCallback(() => {
		const menuItem = getConvertedMenu2Item({ menu, quantity: 1, price: menuPrice, module })
		addMainDish(menuItem)
		close()
	}, [menu, addMainDish, close, menuPrice, module])

	const removeMenuProduct = useCallback((event, i) => {
		event.stopPropagation()
		const menuCopy = clone(menu)
		menuCopy.menuPositions[i].item = null
		dispatch(setCartProductMenu(menuCopy))
	}, [menu, dispatch])

	const reset = useCallback(() => {
		let menuCopy = clone(menu)
		let { menuPositions } = menuCopy
		menuPositions = menuPositions.map(pr => {
			const { item, ...rest } = pr
			return rest
		})
		menuCopy = { ...menuCopy, menuPositions }
		dispatch(setCartProductMenu(menuCopy))
		dispatch(setCartProductMenuProductIndex(0))
	}, [menu, dispatch])

	return (
		<>
			<MenuSelectionWrapper ref={menuSelectionRef} id="menu-selection">
				<MenuSelectionTitle>
					<svg viewBox="0 0 62 49" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M0 43.8736V47.8896C0 48.4336 0.445 48.8756 0.992 48.8756H61.004C61.551 48.8756 61.996 48.4336 61.996 47.8896V43.8736C61.996 43.3316 61.551 42.8886 61.004 42.8886H56.411L57.573 29.0986C57.597 28.8246 57.502 28.5526 57.315 28.3506C57.128 28.1486 56.862 28.0316 56.585 28.0316H55.549V20.0236C55.549 19.4806 55.104 19.0376 54.557 19.0376H42.767L42.904 16.5956H43.797C44.344 16.5956 44.789 16.1536 44.789 15.6106V12.0906C44.789 11.5486 44.344 11.1066 43.797 11.1066H40.42L42.726 5.32161L51.951 1.90861C52.199 1.81561 52.397 1.63361 52.507 1.39361C52.616 1.15461 52.626 0.887609 52.534 0.641609C52.343 0.132609 51.773 -0.128391 51.258 0.0626091L41.619 3.62861C41.356 3.72661 41.146 3.93061 41.043 4.18961L38.286 11.1066H22.102C21.555 11.1066 21.11 11.5486 21.11 12.0906V15.6106C21.11 16.1536 21.555 16.5956 22.102 16.5956H23.001L23.3 21.4056L22.975 21.3346C21.957 21.1126 20.912 20.9996 19.871 20.9996C13.159 20.9996 7.579 25.4896 6.89 31.4446L6.864 31.6656H5.411C4.864 31.6656 4.419 32.1076 4.419 32.6496V36.3296C4.419 36.7676 4.716 37.1566 5.142 37.2756L5.387 37.3446L5.315 37.5876C5.226 37.8896 5.18 38.2016 5.18 38.5156C5.18 39.6456 5.773 40.6986 6.729 41.2636L6.841 41.3296L6.851 41.4606C6.877 41.8266 6.925 42.1976 6.997 42.5956L7.051 42.8886H0.992C0.445 42.8886 0 43.3316 0 43.8736ZM51.648 21.0076H53.565V28.0316H51.648V21.0076ZM47.745 21.0076H49.664V28.0316H47.745V21.0076ZM43.84 21.0076H45.759V28.0316H43.84V21.0076ZM55.451 30.0016L55.117 30.4096C54.061 31.6986 51.064 32.9996 46.752 32.9996C42.44 32.9996 39.443 31.6986 38.386 30.4096L38.052 30.0016H55.451ZM55.263 32.8876L54.42 42.8886H39.085L38.241 32.8886L38.658 33.1316C40.686 34.3166 43.56 34.9686 46.752 34.9686C49.944 34.9686 52.818 34.3156 54.846 33.1306L55.263 32.8876ZM37.094 42.8886H32.67L32.731 42.5896C32.809 42.2076 32.86 41.8386 32.889 41.4606L32.899 41.3306L33.012 41.2646C33.968 40.7006 34.562 39.6466 34.562 38.5156C34.562 38.2036 34.516 37.8926 34.425 37.5896L34.352 37.3456L34.598 37.2776C35.025 37.1586 35.323 36.7696 35.323 36.3296V32.6506C35.323 32.1076 34.878 31.6656 34.331 31.6656H32.874L32.849 31.4446C32.385 27.4386 29.558 23.8696 25.47 22.1286L25.328 22.0686L24.988 16.5956H40.917L40.779 19.0386H38.947C38.4 19.0386 37.955 19.4806 37.955 20.0236V28.0316H36.919C36.642 28.0316 36.376 28.1486 36.189 28.3506C36.001 28.5526 35.907 28.8256 35.93 29.0986L37.094 42.8886ZM41.857 21.0076V28.0316H39.939V21.0076H41.857ZM23.094 14.6256V13.0766H42.805V14.6256H23.094ZM8.899 31.3786C9.621 26.5846 14.338 22.9696 19.871 22.9696C25.404 22.9696 30.121 26.5846 30.843 31.3786L30.887 31.6656H8.855L8.899 31.3786ZM6.403 35.3446V33.6356H33.339V35.3446H6.403ZM8.243 39.7116C7.648 39.7116 7.164 39.1756 7.164 38.5156C7.164 37.8566 7.648 37.3196 8.243 37.3196H31.5C32.094 37.3196 32.578 37.8566 32.578 38.5156C32.578 39.1756 32.094 39.7116 31.5 39.7116H8.243ZM30.885 41.6806L30.834 41.9726C30.792 42.2146 30.737 42.4576 30.67 42.7046L30.619 42.8886H9.088L9.042 42.6976C8.984 42.4566 8.935 42.2126 8.898 41.9676L8.855 41.6806H30.885ZM1.984 44.8586H60.012V46.9056H1.984V44.8586Z" />
					</svg>
					<div className="title">{menu?.title}</div>
					<Price value={menuTotalPrice} />
				</MenuSelectionTitle>
				<MenuProductsList>
					{menu?.menuPositions?.map((menuProduct, i) =>
						menuProduct.item ?
							<MenuItem key={i}
												menuProduct={menuProduct}
												focused={i === menuProductIndex}
												onClick={() => setMenuItemFocus(i, menuProduct.item)}
												onClickMinus={(event) => removeMenuProduct(event, i)} /> :
							<MenuProduct key={i}
													 className={menuProductIndex === i ? 'active' : ''}
													 onClick={() => setMenuProductFocus(i)}>
								<MenuProductTitle className={menuProductIndex === i ? 'active' : ''}>
									<MainIcon icon="no-selected-product" />
									<div className="title">{menuProduct.categoryTitle}</div>
								</MenuProductTitle>
								<div className="size">{menuProduct.priceDescriptionTitle}</div>
							</MenuProduct>)}
				</MenuProductsList>
				<MenuButtonsWrapper>
					{menuNotEmpty ?
						<Button name="back" icon="clear" text={t('buttons.controls.cancel_1')} keyButton="F5"
										zIndex={0} onClick={reset} /> :
						<Button name="back" icon="back" text={t('buttons.controls.back')} keyButton="Escape" keyText="esc"
										zIndex={0} onClick={close} />}
					<Button name="send" icon="checkmark" text={t('Cart.Buttons.addToCart')} keyButton="F2"
									color={colorTheme === 'LIGHT' ? 'blue' : 'green'} zIndex={0} onClick={addMenu} disabled={!menuFinished} />
				</MenuButtonsWrapper>
			</MenuSelectionWrapper>

			{isNeededZIndex &&
				<KeyBindings escape={close} />}
		</>
	)
})

MenuSelection.displayName = 'MenuSelection'
export default MenuSelection
